import {useEffect, useState} from "react";
import {Box, CircularProgress, Grid, Switch, TextField} from "@mui/material";
import moment from "moment";
import _ from "lodash";
import BasicButton from "../../../../common/BasicButton";
import {organizationSettingsService} from "_services";
import {SnackBar} from "components/common";
import {Auth} from "components/Authorization";
import commonStyles from "../commonStyles";

const STATUS_COLORS = {
    GREEN: '#2e7d32',
    YELLOW: '#ed6c02',
    RED: '#d32f2f',
    UNKNOWN: '#9e9e9e',
};

const MAX_COPY_MESSAGES = 1000;
const DEFAULT_COPY_MESSAGES = 100;

function resolveStatusColor(status) {
    const normalized = (status || '').toUpperCase();
    return STATUS_COLORS[normalized] || STATUS_COLORS.UNKNOWN;
}

function formatLastMessage(lastActivityDate, label) {
    if (!lastActivityDate) {
        return label.NO_MESSAGES;
    }
    return moment(lastActivityDate).format('DD MMM YYYY, HH:mm');
}

function clampCount(value) {
    const parsed = parseInt(value, 10);
    if (Number.isNaN(parsed) || parsed < 1) return 1;
    if (parsed > MAX_COPY_MESSAGES) return MAX_COPY_MESSAGES;
    return parsed;
}

function handleApiError(error, fallback) {
    if (_.get(error, 'response.status') === 401) {
        Auth.processLogout();
        return;
    }
    const message = _.get(error, 'response.data.message') || _.get(error, 'message') || fallback;
    SnackBar.showMessage(message);
}

export default function TrendsSection({trendsHandle, label}) {
    const statusLabel = trendsHandle?.status || '-';
    const statusColor = resolveStatusColor(trendsHandle?.status);
    const lastMessage = formatLastMessage(trendsHandle?.lastActivityDate, label);
    const canPerformOperations = trendsHandle?.id != null;

    const [generateResponsesEnabled, setGenerateResponsesEnabled] = useState(
        Boolean(trendsHandle?.shouldGenerateResponsesEnabled)
    );
    const [togglingResponses, setTogglingResponses] = useState(false);
    const [messageCount, setMessageCount] = useState(DEFAULT_COPY_MESSAGES);
    const [copyingMessages, setCopyingMessages] = useState(false);

    useEffect(() => {
        setGenerateResponsesEnabled(Boolean(trendsHandle?.shouldGenerateResponsesEnabled));
    }, [trendsHandle?.shouldGenerateResponsesEnabled]);

    const handleToggleGenerateResponses = async (event) => {
        if (!canPerformOperations || togglingResponses) return;
        const previousValue = Boolean(trendsHandle?.shouldGenerateResponsesEnabled);
        const nextValue = event.target.checked;
        setTogglingResponses(true);
        setGenerateResponsesEnabled(nextValue);
        try {
            const response = await organizationSettingsService.updateTrendsDataSourceHandle(
                trendsHandle.id,
                {shouldGenerateResponsesEnabled: nextValue}
            );
            const updated = _.get(response, 'data.shouldGenerateResponsesEnabled');
            if (typeof updated === 'boolean') {
                setGenerateResponsesEnabled(updated);
            }
            SnackBar.showMessage(
                nextValue ? 'Response generation enabled.' : 'Response generation disabled.'
            );
        } catch (error) {
            setGenerateResponsesEnabled(previousValue);
            handleApiError(error, 'Failed to update response generation setting.');
        } finally {
            setTogglingResponses(false);
        }
    };

    const handleCopyMessages = async () => {
        if (!canPerformOperations || copyingMessages) return;
        const count = clampCount(messageCount);
        setMessageCount(count);
        setCopyingMessages(true);
        try {
            const response = await organizationSettingsService.copyTrendsDataSourceHandleMessagesToTeams(
                trendsHandle.id,
                count
            );
            const eligibleMessageCount = _.get(response, 'data.eligibleMessageCount');
            const text = typeof eligibleMessageCount === 'number'
                ? `Sent ${eligibleMessageCount} message(s) for response generation.`
                : 'Messages copied to Teams.';
            SnackBar.showMessage(text);
        } catch (error) {
            handleApiError(error, 'Failed to copy messages to Teams.');
        } finally {
            setCopyingMessages(false);
        }
    };

    return (
        <Box sx={styles.container}>
            <Box component={'h4'} sx={commonStyles.labelBold}>
                {label.TRENDS}
            </Box>
            <Grid container columnSpacing={4} rowSpacing={1}>
                <Grid item xs={12} md={4}>
                    <Box sx={commonStyles.labelMedium}>{label.STATUS}</Box>
                    <Box sx={styles.statusValue}>
                        <Box sx={{...styles.statusDot, backgroundColor: statusColor}} />
                        <Box component={'span'}>{statusLabel}</Box>
                    </Box>
                </Grid>
                <Grid item xs={12} md={8}>
                    <Box sx={commonStyles.labelMedium}>{label.LAST_MESSAGE}</Box>
                    <Box sx={styles.lastMessage}>{lastMessage}</Box>
                </Grid>
                {canPerformOperations && (
                    <>
                        <Grid item xs={12} md={4} sx={styles.operationsRowSpacing}>
                            <Box sx={commonStyles.labelMedium}>{label.GENERATE_RESPONSES}</Box>
                            <Box sx={styles.toggleRow}>
                                <Switch
                                    checked={generateResponsesEnabled}
                                    disabled={togglingResponses}
                                    onChange={handleToggleGenerateResponses}
                                />
                                {togglingResponses && <CircularProgress size={16} sx={styles.spinner} />}
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={8} sx={styles.operationsRowSpacing}>
                            <Box sx={commonStyles.labelMedium}>{label.COPY_MESSAGES_TO_TEAMS}</Box>
                            <Box sx={styles.copyMessagesRow}>
                                <TextField
                                    type={'number'}
                                    size={'small'}
                                    label={label.MESSAGE_COUNT}
                                    value={messageCount}
                                    disabled={copyingMessages}
                                    onChange={(event) => setMessageCount(event.target.value)}
                                    onBlur={() => setMessageCount(clampCount(messageCount))}
                                    inputProps={{min: 1, max: MAX_COPY_MESSAGES, step: 1}}
                                    sx={styles.countField}
                                />
                                <Box sx={styles.copyButton}>
                                    <BasicButton
                                        label={copyingMessages ? 'Copying…' : label.COPY}
                                        action={handleCopyMessages}
                                        disabled={copyingMessages}
                                        endIcon={copyingMessages
                                            ? <CircularProgress size={16} sx={styles.spinner} />
                                            : null}
                                    />
                                </Box>
                            </Box>
                        </Grid>
                    </>
                )}
            </Grid>
        </Box>
    );
}

const styles = {
    container: {
        mb: 2,
    },
    statusValue: {
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        mt: 0.5,
    },
    statusDot: {
        width: '12px',
        height: '12px',
        borderRadius: '50%',
        display: 'inline-block',
    },
    lastMessage: {
        mt: 0.5,
    },
    operationsRowSpacing: {
        mt: 1,
    },
    toggleRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        mt: 0.5,
    },
    copyMessagesRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        mt: 0.5,
    },
    countField: {
        width: '160px',
    },
    copyButton: {
        width: '120px',
    },
    spinner: {
        color: '#003300',
    },
};
